import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export const useSocial = () => {
    const { user } = useAuth();
    const [friends, setFriends] = useState([]);
    const [requests, setRequests] = useState([]); // Incoming requests
    const [messages, setMessages] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [friendCount, setFriendCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch Friends and Requests
    const fetchFriends = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);

        try {
            // 1. Fetch raw friendship data (no joins)
            const { data: sent, error: sentError } = await supabase
                .from('friendships')
                .select('id, friend_id, status, created_at')
                .eq('user_id', user.id)
                .eq('status', 'accepted');

            if (sentError) throw sentError;

            const { data: received, error: receivedError } = await supabase
                .from('friendships')
                .select('id, user_id, status, created_at')
                .eq('friend_id', user.id)
                .eq('status', 'accepted');

            if (receivedError) throw receivedError;

            const { data: incoming, error: incomingError } = await supabase
                .from('friendships')
                .select('id, user_id, status, created_at')
                .eq('friend_id', user.id)
                .eq('status', 'pending');

            if (incomingError) throw incomingError;

            // 2. Collect all User IDs to fetch profiles for
            const profileIds = new Set([
                ...(sent || []).map(f => f.friend_id),
                ...(received || []).map(f => f.user_id),
                ...(incoming || []).map(f => f.user_id)
            ]);

            if (profileIds.size > 0) {
                const { data: profiles, error: profilesError } = await supabase
                    .from('profiles')
                    .select('id, username, call_sign, country')
                    .in('id', Array.from(profileIds));

                if (profilesError) throw profilesError;

                // Create a map for easy lookup
                const profileMap = (profiles || []).reduce((acc, p) => {
                    acc[p.id] = p;
                    return acc;
                }, {});

                // 3. Reconstruct the lists with profile data
                const allFriends = [
                    ...(sent || []).map(f => ({ ...profileMap[f.friend_id], ...f, isSender: true, friendUserId: f.friend_id })),
                    ...(received || []).map(f => ({ ...profileMap[f.user_id], ...f, isSender: false, friendUserId: f.user_id }))
                ];

                // Deduplicate friends by friendUserId (keep only one entry per unique friend)
                const seenFriendIds = new Set();
                const uniqueFriends = allFriends.filter(friend => {
                    if (seenFriendIds.has(friend.friendUserId)) {
                        return false; // Skip duplicates
                    }
                    seenFriendIds.add(friend.friendUserId);
                    return true;
                });

                setFriends(uniqueFriends);

                const formattedRequests = (incoming || []).map(r => ({ ...profileMap[r.user_id], ...r }));
                setRequests(formattedRequests);
            } else {
                setFriends([]);
                setRequests([]);
            }

        } catch (err) {
            console.error('Error in fetchFriends:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Send Friend Request
    const sendFriendRequest = useCallback(async (targetUserId) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('friendships')
                .insert({ user_id: user.id, friend_id: targetUserId, status: 'pending' });

            if (error) throw error;
            return { success: true };
        } catch (err) {
            console.error('Error sending friend request:', err);
            return { error: err.message };
        }
    }, [user]);

    // Accept Friend Request
    const acceptFriendRequest = useCallback(async (friendshipId) => {
        try {
            const { error } = await supabase
                .from('friendships')
                .update({ status: 'accepted' })
                .eq('id', friendshipId);

            if (error) throw error;
            fetchFriends(); // Refresh list
            return { success: true };
        } catch (err) {
            console.error('Error accepting friend request:', err);
            return { error: err.message };
        }
    }, [fetchFriends]);

    // Search Users
    const searchUsers = useCallback(async (query) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, username, call_sign, country')
                .or(`username.ilike.%${query}%,call_sign.ilike.%${query}%`)
                .limit(10);

            if (error) throw error;
            return { data: data.filter(u => u.id !== user?.id), error: null };
        } catch (err) {
            console.error('Error searching users:', err);
            return { data: [], error: err.message };
        }
    }, [user]);

    // Fetch Messages
    const fetchMessages = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            // 1. Fetch messages
            const { data: msgs, error: msgError } = await supabase
                .from('messages')
                .select('*')
                .eq('receiver_id', user.id)
                .order('created_at', { ascending: false });

            if (msgError) throw msgError;

            // 2. Fetch Sender Profiles
            const senderIds = new Set((msgs || []).map(m => m.sender_id));

            if (senderIds.size > 0) {
                const { data: profiles, error: profError } = await supabase
                    .from('profiles')
                    .select('id, username, call_sign')
                    .in('id', Array.from(senderIds));

                if (profError) throw profError;

                const profileMap = (profiles || []).reduce((acc, p) => {
                    acc[p.id] = p;
                    return acc;
                }, {});

                // 3. Merge
                const formattedMessages = (msgs || []).map(m => ({
                    ...m,
                    sender: profileMap[m.sender_id] || { username: 'Unknown', call_sign: '???' }
                }));
                setMessages(formattedMessages);
            } else {
                setMessages([]);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Fetch Challenges
    const fetchChallenges = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data: challs, error: cError } = await supabase
                .from('challenges')
                .select('*')
                .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
                .order('created_at', { ascending: false });

            if (cError) throw cError;

            // Fetch Profiles involved
            const uIds = new Set();
            challs?.forEach(c => {
                uIds.add(c.sender_id);
                uIds.add(c.receiver_id);
            });

            if (uIds.size > 0) {
                const { data: profiles, error: pError } = await supabase
                    .from('profiles')
                    .select('id, username, call_sign')
                    .in('id', Array.from(uIds));

                if (pError) throw pError;

                const pMap = (profiles || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});

                const formatted = (challs || []).map(c => ({
                    ...c,
                    sender: pMap[c.sender_id] || { username: 'Unknown' },
                    receiver: pMap[c.receiver_id] || { username: 'Unknown' },
                    isIncoming: c.receiver_id === user.id
                }));
                console.log('Fetched challenges from DB:', formatted);
                setChallenges(formatted);
            } else {
                setChallenges([]);
            }
        } catch (err) {
            console.error('Error fetching challenges:', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Send Message
    const sendMessage = useCallback(async (receiverId, content) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('messages')
                .insert({
                    sender_id: user.id,
                    receiver_id: receiverId,
                    content: content,
                    is_read: false
                });

            if (error) throw error;
            return { success: true };
        } catch (err) {
            console.error('Error sending message:', err);
            return { error: err.message };
        }
    }, [user]);

    // Mark Message Read
    const markMessageRead = useCallback(async (messageId) => {
        try {
            const { error } = await supabase
                .from('messages')
                .update({ is_read: true })
                .eq('id', messageId);

            if (error) throw error;
            // Update local state
            setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true } : m));
        } catch (err) {
            console.error('Error marking message read:', err);
        }
    }, []);

    // Send Challenge
    const sendChallenge = useCallback(async (receiverId, type, targetValue) => {
        try {
            const { error } = await supabase
                .from('challenges')
                .insert({
                    sender_id: user.id,
                    receiver_id: receiverId,
                    type,
                    target_value: targetValue,
                    status: 'pending'
                });
            if (error) throw error;
            return { success: true };
        } catch (err) {
            console.error('Error sending challenge:', err);
            return { error: err.message };
        }
    }, [user]);

    // Update Challenge Status
    const updateChallengeStatus = useCallback(async (challengeId, status) => {
        try {
            const { error } = await supabase
                .from('challenges')
                .update({ status })
                .eq('id', challengeId);
            if (error) throw error;
            fetchChallenges();
            return { success: true };
        } catch (err) {
            return { error: err.message };
        }
    }, [fetchChallenges]);

    // Fetch Unread Counts (Messages + Requests)
    const fetchUnreadCounts = useCallback(async () => {
        if (!user) return { unreadCount: 0, friendCount: 0 };
        try {
            const { count: msgCount, error: msgError } = await supabase
                .from('messages')
                .select('*', { count: 'exact', head: true })
                .eq('receiver_id', user.id)
                .eq('is_read', false);

            if (msgError) throw msgError;

            const { count: reqCount, error: reqError } = await supabase
                .from('friendships')
                .select('*', { count: 'exact', head: true })
                .eq('friend_id', user.id)
                .eq('status', 'pending');

            if (reqError) throw reqError;

            const { count: fCount, error: fError } = await supabase
                .from('friendships')
                .select('*', { count: 'exact', head: true })
                .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`)
                .eq('status', 'accepted');

            if (fError) throw fError;

            const totalUnread = (msgCount || 0) + (reqCount || 0);
            setUnreadCount(totalUnread);
            setFriendCount(fCount || 0);

            return { unreadCount: totalUnread, friendCount: fCount || 0 };
        } catch (err) {
            console.error('Error fetching unread counts:', err);
            return { unreadCount: 0, friendCount: 0 };
        }
    }, [user]);

    return {
        friends,
        requests,
        messages,
        unreadCount,
        friendCount,
        loading,
        error,
        fetchFriends,
        sendFriendRequest,
        acceptFriendRequest,
        searchUsers,
        fetchMessages,
        sendMessage,
        markMessageRead,
        fetchUnreadCounts,
        challenges,
        fetchChallenges,
        sendChallenge,
        updateChallengeStatus
    };
};
